import { Suspense, createElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useRoutes, type NavigateFunction, type RouteObject } from 'react-router-dom';
import { getCachedCurrentUserMenuRoutes, getCurrentUserMenuRoutes, type MenuRoute } from '@/api/system';
import { isAuthenticated } from '@/utils/auth';
import NotFound from '../pages/NotFound';
import staticRoutes from './config';
import { joinMenuPath, resolveMenuComponent, resolveMenuPath } from './dynamic';

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

function buildMenuRoutes(menuRoutes: MenuRoute[], parentPath = ''): RouteObject[] {
  return menuRoutes.flatMap((route) => {
    const databasePath = joinMenuPath(parentPath, route.path);
    const Component = resolveMenuComponent(route.component);
    const current: RouteObject[] = Component
      ? [{ path: resolveMenuPath(route, parentPath), element: createElement(Component) }]
      : [];
    return [...current, ...buildMenuRoutes(route.children || [], databasePath)];
  });
}

export function AppRoutes() {
  const authed = isAuthenticated();
  const cachedRoutes = getCachedCurrentUserMenuRoutes();
  const [menuRoutes, setMenuRoutes] = useState<MenuRoute[]>(() => cachedRoutes || []);
  const [loading, setLoading] = useState(authed && !cachedRoutes);
  const routes = useMemo<RouteObject[]>(() => [
    ...staticRoutes,
    ...buildMenuRoutes(menuRoutes),
    { path: '*', element: createElement(NotFound) },
  ], [menuRoutes]);
  const element = useRoutes(routes);
  const navigate = useNavigate();

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  }, [navigate]);

  useEffect(() => {
    if (!authed) {
      setLoading(false);
      setMenuRoutes([]);
      return;
    }
    let active = true;
    const loadRoutes = () => {
      setLoading(true);
      getCurrentUserMenuRoutes()
        .then((data) => { if (active) setMenuRoutes(data); })
        .finally(() => { if (active) setLoading(false); });
    };
    loadRoutes();
    window.addEventListener('menu-routes-changed', loadRoutes);
    return () => {
      active = false;
      window.removeEventListener('menu-routes-changed', loadRoutes);
    };
  }, [authed]);

  if (loading) return createElement(RouteLoading);
  return createElement(Suspense, { fallback: createElement(RouteLoading) }, element);
}

function RouteLoading() {
  return createElement('div', { className: 'min-h-screen flex items-center justify-center bg-[#f0f2f5]' },
    createElement('div', { className: 'w-8 h-8 border-2 border-[#122c52] border-t-transparent rounded-full animate-spin' }));
}
